import React, { useEffect } from "react"
import { View } from "react-native"
import { connect } from "react-redux"
import { useNavigation, useRoute } from "@react-navigation/native"
import { ActivityIndicator } from "react-native-paper"
import * as amplitude from "@amplitude/analytics-react-native"
import TextM from "@common/components/text/TextM"
import FilledButtonM from "@common/components/button/FilledButtonM"
import TextButtonM from "@common/components/button/TextButtonM"
import { theme } from "@common/theme/theme"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { apiRequest } from "@data/redux/actions/api"
import {
    normaliseClass,
    deriveClassRentree,
} from "../summer/constants/summerContent"
import { stageFunnelService } from "./services/stageFunnelService"
import { useStageFunnel } from "./context/StageFunnelContext"
import { isUnreadableResumeLink } from "@common/utils/funnelLinkIdentity"
import { useResumeFailureReport } from "@common/hooks/useResumeFailureReport"
import {
    clearUserFromUrl,
    readSourceParam,
    readTemplateParam,
    readUserParam,
} from "./hooks/useStageFunnelGuard"

/**
 * Admin-issued magic link landing: GET /stage/resume?user=<base64email>.
 *
 * Calls the resume endpoint (behind `auth_or_email`), hydrates the funnel
 * Context with the persisted stage FastTrackState, then navigation.reset's to
 * the step matching the current state. Pendant du SummerResumeScreen.
 */
// Map each state to the full stack history so navigation.goBack() walks back
// through the previous funnel steps natively. The LAST entry is the step the
// lead resumes on :
//   stage_started               → diagnostic à compléter
//   stage_diag_completed        → configurateur (sessions/matières/horaire) à régler
//   stage_checkout_created      → paiement à finaliser
//   stage_paid / _attribution_pending → confirmation (parent reconsulte)
//   stage_failed                → retour au checkout pour retenter le paiement
// `null` covers a user with no stage FastTrackState (orphan / autres funnels) —
// lands on stage/start with hydrated user info from /resume.
const STATE_TO_STACK = {
    stage_started: ["stage/start", "stage/diagnostic"],
    stage_diag_completed: [
        "stage/start",
        "stage/diagnostic",
        "stage/configurator",
    ],
    // Même état, mais un programme a déjà été composé : le parent avait
    // dépassé le compositeur — il reprend sur son programme, pas avant.
    stage_diag_completed_composed: [
        "stage/start",
        "stage/diagnostic",
        "stage/configurator",
        "stage/plan",
    ],
    stage_checkout_created: [
        "stage/start",
        "stage/diagnostic",
        "stage/configurator",
        "stage/plan",
        "stage/tutors",
        "stage/checkout",
    ],
    stage_failed: [
        "stage/start",
        "stage/diagnostic",
        "stage/configurator",
        "stage/plan",
        "stage/tutors",
        "stage/checkout",
    ],
    stage_paid: ["stage/confirmation"],
    stage_attribution_pending: ["stage/confirmation"],
    null: ["stage/start"],
}

const TERMINAL_PAID_STATES = ["stage_paid", "stage_attribution_pending"]

// Les étapes qu'un abonné reconnu ne voit pas : le serveur connaît sa classe
// et ses spécificités.
const KNOWN_PROFILE_STEPS = ["stage/start", "stage/diagnostic"]

/**
 * La pile reconstruite pour un état repris. Sans parcours, l'écran de départ
 * crée le parcours et décide lui-même ; avec un parcours, un abonné reconnu
 * reprend sans les deux étapes sautées — pas de retour vers elles.
 */
export const resumeStack = (stackKey, profileKnown) => {
    const stack = STATE_TO_STACK[stackKey] || ["stage/start"]
    if (!profileKnown || stackKey === "null") return stack
    const kept = stack.filter((name) => !KNOWN_PROFILE_STEPS.includes(name))
    return kept.length ? kept : ["stage/configurator"]
}

// Source d'un lien d'entrée qui n'en porte pas — le mot du funnel
// d'inscription pour la même porte (`FastTrackState::ENTRY_SOURCE_RESUME_LINK`).
const LINK_SOURCE = "resume_link"

// COUTURE DE NORMALISATION UNIQUE du funnel stage : borne les types et
// backfille les champs manquants depuis la source la plus fiable pour que la
// reprise marche quel que soit l'âge/la complétude du checkout_params.
const mapStateCheckoutParamsToContext = (
    state,
    checkoutParams,
    user,
    templateParam
) => {
    const cp = checkoutParams || {}
    const studentClass =
        normaliseClass(cp.student_class || user?.student_class) || null
    return {
        funnelStateId: state.funnel_state_id || null,
        // Le prénom LIVE (corrigé au BO) prime sur le snapshot figé.
        studentFirstname: user?.firstname || cp.firstname || "",
        parentEmail: user?.email || "",
        submissionGUID: cp.submission_guid || null,
        leadIntent: cp.lead_intent || null,
        // Le serveur ne rend qu'un parcours du stage que le lien vend : son
        // slug et celui du lien désignent donc le même stage. Sans parcours,
        // c'est le stage résolu par le serveur, à défaut celui du lien.
        templateSlug:
            cp.template_slug || state.template_slug || templateParam || null,
        studentClass,
        studentClassRentree:
            cp.student_class_rentree ||
            deriveClassRentree(studentClass) ||
            null,
        objective: cp.objective || null,
        niveauActuel: cp.niveau_actuel || null,
        besoinsSpeciaux: cp.besoins_speciaux || [],
        programDecidedLater: !!cp.program_decided_later,
        // Composition par sessions (contrat v2) : dates ISO cochées, triées, et
        // matières PAR session. Ids matières coercés en string dès
        // l'hydratation (les chips comparent en string, le backend persiste en
        // int). Re-validées en aval contre le catalogue vivant (une reprise
        // tardive écarte les sessions qui ne sont plus offertes).
        sessions: (Array.isArray(cp.sessions) ? cp.sessions : [])
            .filter(Boolean)
            .map(String)
            .sort(),
        sessionSubjects: Object.fromEntries(
            Object.entries(cp.session_subjects || {}).map(([date, ids]) => [
                date,
                (Array.isArray(ids) ? ids : []).map(String),
            ])
        ),
        slotKey: cp.slot_key || null,
        paid: TERMINAL_PAID_STATES.includes(state.state),
        profileKnown: !!state.profile_known,
    }
}

const StageResumeScreen = (props) => {
    const navigation = useNavigation()
    const route = useRoute()
    const { update, reset } = useStageFunnel()

    const userParam = readUserParam(route)
    const templateParam = readTemplateParam(route)
    const sourceParam = readSourceParam(route)

    const resume = () => {
        // Le stage visé part avec l'identité : c'est lui qui dit au serveur
        // quel parcours reprendre (jamais celui d'un autre stage).
        const query = { user: userParam }
        if (templateParam) query.template = templateParam
        props.apiRequest(stageFunnelService.resumeStage, {}, {}, query)
    }

    useEffect(() => {
        if (!userParam) return
        // Reset first so the Context doesn't keep stale values from a previous
        // lead sharing the same browser tab (admin-on-laptop flow).
        reset()
        resume()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userParam])

    useApiStatus(stageFunnelService.resumeStage, null, false, (data) => {
        if (!data) return
        const stateName = data.state
        const ctx = mapStateCheckoutParamsToContext(
            data,
            data.checkout_params,
            data.user,
            templateParam
        )
        // `resumedToken` voyage dans le MÊME patch que l'hydratation : la garde
        // saute la reprise pour un jeton déjà arbitré. Sans lui, une famille
        // SANS parcours serait renvoyée du début vers la reprise en boucle.
        update({
            ...ctx,
            // Une arrivée par lien se distingue du formulaire même quand le
            // lien ne dit pas d'où il vient (lien copié du BO, relance).
            entrySource: sourceParam || LINK_SOURCE,
            resumedToken: userParam,
            resumedTemplate: templateParam || null,
        })

        // Track the magic-link "fresh start" branch (state=null = user has
        // no journey on the stage this link sells).
        if (stateName === null) {
            amplitude.track("stage_resume_fresh_start", {
                has_user: !!data.user?.email,
                has_student_class: !!data.user?.student_class,
                template_slug: ctx.templateSlug,
                source: sourceParam || LINK_SOURCE,
            })
        }

        const composed =
            stateName === "stage_diag_completed" &&
            Object.keys(data.checkout_params?.program_study_program_ids ?? {})
                .length > 0
        const stackKey =
            stateName === null
                ? "null"
                : composed
                  ? "stage_diag_completed_composed"
                  : stateName
        const stack = resumeStack(stackKey, ctx.profileKnown)
        // Carry `user` onto EVERY route of the rebuilt stack — a goBack()
        // onto an intermediate step (or a refresh while on it) must still
        // find the `?user=` token to stay shareable and refresh-resumable.
        // Le slug résolu (état persisté, sinon lien) voyage avec `user` sur
        // chaque route : un goBack() ou un refresh sur une étape doit encore
        // savoir de quel stage il s'agit.
        const templateSlug = ctx.templateSlug || null
        navigation.reset({
            index: stack.length - 1,
            routes: stack.map((name) => ({
                name,
                params: templateSlug
                    ? { user: userParam, template: templateSlug }
                    : { user: userParam },
            })),
        })
    })

    // « Réessayer » sur un lien que le serveur ne reconnaît pas ne peut jamais
    // aboutir : seul un échec passager le propose. L'échec se traite sur place,
    // la garde renverrait ici un parent reparti vers le début.
    const unreadableLink = isUnreadableResumeLink(userParam, props.error)

    useResumeFailureReport("stage", props.error, {
        unreadable: unreadableLink,
        template: templateParam,
    })

    if (unreadableLink || props.error) {
        return (
            <View style={styles.container}>
                <TextM
                    color={theme.colors.error}
                    wrap
                    style={styles.errorText}
                    fontWeight="Bold"
                    fontSize={14}
                >
                    {unreadableLink
                        ? "Ce lien n'est plus valide"
                        : "Impossible de reprendre votre inscription pour le moment."}
                </TextM>
                {!unreadableLink && (
                    <FilledButtonM onPress={resume} label="Réessayer" />
                )}
                <TextButtonM
                    onPress={() => {
                        // Sortie volontaire du parcours repris : sans le
                        // nettoyage, la garde relit `?user=` dans l'URL web
                        // et renvoie ici. Le stage visé, lui, reste.
                        clearUserFromUrl()
                        navigation.replace(
                            "stage/start",
                            templateParam
                                ? { template: templateParam }
                                : undefined
                        )
                    }}
                    label="Reprendre depuis le début"
                />
            </View>
        )
    }

    return (
        <View style={styles.container}>
            <ActivityIndicator
                color={theme.colors.primary}
                style={{ marginTop: 30 }}
            />
            <TextM wrap>Reprise du parcours en cours…</TextM>
        </View>
    )
}

const styles = {
    container: {
        alignItems: "center",
        flex: 1,
        padding: 20,
    },
    errorText: {
        marginTop: 20,
        marginBottom: 20,
        textAlign: "center",
    },
}

const mapStateToProps = (state) => ({
    error: state.api?.resumeStage?.error,
})

const mapDispatchToProps = {
    apiRequest,
}

export default connect(mapStateToProps, mapDispatchToProps)(StageResumeScreen)
